import { __assign, __extends } from "tslib";
import * as React from 'react';
import { TextFieldBase, } from '@fluentui/react/lib/TextField';
import { getStyles } from './AppInput.styles';
import { styled, KeyCodes } from '@fluentui/react/lib/Utilities';
import { AppTooltipHost } from '@ms/acui-tooltip/lib/AppTooltipHost';
import { KeytipData } from '@fluentui/react/lib/KeytipData';
import { dataUniqueId, defaultKeytipExecute, deriveInputKeytip } from '@ms/acui-common/lib/utilities';
import { ribbonExperiments, checkExperiment } from '@ms/acui-common/lib/experiments';
import { AppContext } from '@ms/acui-context/lib/AppContext';
export var AppBaseInput = styled(TextFieldBase, getStyles);
var AppInput = /** @class */ (function (_super) {
    __extends(AppInput, _super);
    function AppInput(props) {
        var _this = _super.call(this, props) || this;
        _this.textField = React.createRef();
        _this.keytipExecute = function (executeTarget) {
            defaultKeytipExecute(executeTarget);
        };
        // Resets the state to initial state
        _this.resetState = function () {
            _this.setState({ text: _this.initialText });
            _this.props.onChange && _this.props.onChange(_this.initialText);
        };
        _this.focus = function () {
            if (!_this.textField || !_this.textField.current) {
                return;
            }
            _this.props.selectTextOnFocus ? _this.textField.current.select() : _this.textField.current.focus();
            _this.initialText = _this.value;
        };
        _this.select = function () {
            var _a;
            (_a = _this.textField.current) === null || _a === void 0 ? void 0 : _a.select();
        };
        _this.blur = function () {
            _this.props.onBlur && _this.props.onBlur();
            if (_this.props.executeOnBlur) {
                _this.validateAndExecute();
            }
            _this.initialText = undefined;
        };
        _this.onChange = function (_ev, newValue) {
            if (!_this.props.readOnly) {
                _this.setState({ text: newValue });
                _this.props.onChange && _this.props.onChange(newValue);
            }
        };
        _this.onKeyDown = function (ev) {
            _this.props.onKeyDown && _this.props.onKeyDown(ev);
            if (ev.keyCode === KeyCodes.enter && _this.props.executeOnEnter) {
                ev.stopPropagation();
                ev.preventDefault();
                _this.validateAndExecute();
            }
        };
        _this.validateAndExecute = function () {
            _this.props.onExecute &&
                _this.value !== undefined &&
                _this.value !== null &&
                _this.props.value !== _this.value &&
                _this.props.onExecute(_this.value);
        };
        _this.state = {
            previousValue: props.value,
        };
        return _this;
    }
    AppInput.prototype.render = function () {
        var _this = this;
        var _a = this.props, placeholder = _a.placeholder, value = _a.value, onRenderSuffix = _a.onRenderSuffix, styles = _a.styles, spellCheck = _a.spellCheck, autoComplete = _a.autoComplete, disabled = _a.disabled, role = _a.role, title = _a.title, label = _a.label, defaultValue = _a.defaultValue, multiline = _a.multiline, resizable = _a.resizable, maxLength = _a.maxLength, keytipProps = _a.keytipProps, readOnly = _a.readOnly;
        var text = '';
        if (defaultValue !== undefined || value === undefined) {
            // if we have a default value, that means the component should be uncontrolled
            text = undefined;
        }
        else if (this.state.text || this.state.text === '') {
            text = this.state.text;
        }
        else {
            text = value;
        }
        // Explicitly default to text because if we pass 'undefined' it will be unset.
        // Note: Fabric also sets the default here to text but 'undefined' overrides it.
        var type = this.props.inputType || 'text';
        return (React.createElement(KeytipData, { keytipProps: deriveInputKeytip(keytipProps, this.keytipExecute) }, function (keytipAttributes) { return (React.createElement(AppBaseInput, __assign({}, keytipAttributes, { componentRef: _this.textField, placeholder: placeholder, value: text, defaultValue: defaultValue, onRenderSuffix: onRenderSuffix, styles: styles, spellCheck: spellCheck, autoComplete: autoComplete, onChange: _this.onChange, onKeyDown: _this.onKeyDown, role: role, title: title, disabled: disabled, "data-unique-id": dataUniqueId(_this.props), onFocus: _this.focus, label: _this.props.hideLabel ? undefined : label, multiline: multiline, resizable: resizable, onBlur: _this.blur, maxLength: maxLength, id: _this.props.id, type: type, ariaLabel: _this.props.label || _this.props.customTooltip, readOnly: readOnly || false, errorMessage: _this.props.errorMessage }))); }));
    };
    AppInput.prototype.componentDidMount = function () {
        var toolTip = this.props.customTooltip || (this.props.hideLabel && this.props.label);
        if (this.context.setTooltipElement && this.context.updateTooltipsHidden && toolTip) {
            this.tooltipHost = new AppTooltipHost({
                updateTooltipsHidden: this.context.updateTooltipsHidden,
                setTooltipElement: this.context.setTooltipElement,
                refElement: this.textField,
                controlId: this.props.id,
                controlLabel: toolTip,
                tooltipDirection: this.props.tooltipDirection,
                performSetOnNextFrame: !!checkExperiment(this.context, ribbonExperiments.delayRenderTooltipsIsEnabled),
            });
            this.tooltipHost.registerTooltipHostActions();
        }
    };
    AppInput.prototype.componentWillUnmount = function () {
        if (this.tooltipHost) {
            this.tooltipHost.unregisterTooltipHostActions();
        }
    };
    AppInput.prototype.componentDidUpdate = function (prevProps) {
        var toolTip = this.props.customTooltip || this.props.label;
        if (this.tooltipHost && prevProps.customTooltip !== this.props.customTooltip && toolTip) {
            this.tooltipHost.updateTooltip(toolTip);
        }
    };
    Object.defineProperty(AppInput.prototype, "value", {
        get: function () {
            return this.textField && this.textField.current ? this.textField.current.value : undefined;
        },
        enumerable: true,
        configurable: true
    });
    AppInput.getDerivedStateFromProps = function (nextProps, state) {
        // There's a gap between when we update the input value with the new selected value where an IME keyboard
        // could add unintended text into the input for a controlled input. To fix this, we set a state that
        // will show the value we submitted until we get the new prop and remove the state value.
        if (nextProps.defaultValue === undefined && state.previousValue !== nextProps.value) {
            return {
                previousValue: nextProps.value,
                text: undefined,
            };
        }
        return null;
    };
    // This allows us to access AppContext directly from this.context
    AppInput.contextType = AppContext;
    return AppInput;
}(React.PureComponent));
export { AppInput };
//# sourceMappingURL=AppInput.js.map